import { useTranslation } from "react-i18next";
import { BsClock } from "react-icons/bs";
import UiButton from "../../../../components/button/Button";
import UiLink from "../../../../components/link/Link.component";
import { IReminder } from "../../../../models/entities/reminder.model";
import { reminderActionButtons } from "../../../../models/pages/participants-reminder.model";
import { HelperService } from "../../../../services/helpers/helper.service";
import { reminderParticipantName } from "./reminders.service";

/**
 * One reminder in the Home worklist (KAN-2555).
 *
 * The pencil and Resolve buttons are the generic Reminders tab's own buttons,
 * built from the shared `reminderActionButtons` spec — same classes, same label,
 * no hover tooltip (Maria, 2026-09-22). The pencil opens the shared
 * `ModalReminder` in UPDATE mode (rendered once at section level), and Resolve
 * runs the same update the global Reminders table runs.
 */

export interface ReminderRowProps {
	reminder: IReminder;
	/** Page-wide row number, 1-based — continues across worklist sections. */
	index: number;
	/** True when this row is due on the coach's local date (renders "Today"). */
	isDueToday: boolean;
	onEdit: (reminder: IReminder) => void;
	onResolve: (reminder: IReminder) => void;
	isResolving: boolean;
}

/**
 * The due date, rendered from the value's UTC components — the same
 * `timeZone: "UTC"` the global Reminders table uses. `dateOn` is a floating
 * calendar date; formatting it in local time would shift it by a day for
 * anyone west of UTC.
 */
const formatDueDate = (dateOn: string, locale: string): string => {
	const parsed = new Date(dateOn);
	if (Number.isNaN(parsed.getTime())) return "";
	return parsed.toLocaleDateString(locale, {
		day: "2-digit",
		month: "2-digit",
		year: "2-digit",
		timeZone: "UTC",
	});
};

const ReminderRow = ({
	reminder,
	index,
	isDueToday,
	onEdit,
	onResolve,
	isResolving,
}: ReminderRowProps) => {
	const { t, i18n } = useTranslation("common");

	const name = reminderParticipantName(reminder);
	const routeId = HelperService.getParticipantRouteId(reminder.patientId, reminder.kannactId);

	// The pencil and Resolve are the generic Reminders tab's own buttons, built
	// from the one shared spec — same classes, same label, no extras (KAN-2555).
	const [editButton, resolveButton] = reminderActionButtons(
		() => onEdit(reminder),
		() => onResolve(reminder),
		isResolving
	);

	return (
		<li
			className="worklist-row worklist-row--reminder"
			data-testid={`reminder-row-${reminder.id}`}
		>
			<span className="worklist-row__index">{index}</span>

			<div className="worklist-row__body">
				<div className="worklist-row__title">
					{/* Per Maria (KAN-2554): ONLY the Kannact ID is the link, the name is
						plain text — the same pattern as the critical-alert rows, the
						appointment cards and the rest of the platform. */}
					<span className="worklist-row__participant">
						{reminder.kannactId &&
							(routeId ? (
								<UiLink
									id={`reminder-participant-${reminder.id}`}
									to={`/participant/${routeId}/dashboard`}
									label={reminder.kannactId}
								/>
							) : (
								<span className="kannact-id">{reminder.kannactId}</span>
							))}
						{name && <span className="full-name">{name}</span>}
					</span>
				</div>

				<div className="worklist-row__meta">
					<span className="worklist-row__reason">{reminder.reason}</span>
					<span className="worklist-row__age">
						<BsClock />
						{isDueToday
							? t("HOME.WORKLIST.REMINDERS.DUE_TODAY")
							: formatDueDate(reminder.dateOn, i18n.language)}
					</span>
				</div>
			</div>

			<div className="worklist-row__actions">
				<UiButton
					{...editButton}
					id={`reminder-edit-${reminder.id}`}
					type="button"
					testId={`reminder-edit-${reminder.id}`}
				/>
				<UiButton
					{...resolveButton}
					id={`reminder-resolve-${reminder.id}`}
					type="button"
					testId={`reminder-resolve-${reminder.id}`}
				/>
			</div>
		</li>
	);
};

export default ReminderRow;
